/**
 * Утилиты для шифрования/дешифрования данных в Storage
 * Использует Web Crypto API для безопасного шифрования на стороне клиента
 */

// Генерация ключа на основе fingerprint браузера и статического секрета
const generateKey = async (): Promise<CryptoKey> => {
  // Создаем уникальный fingerprint на основе характеристик браузера
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    new Date().getTimezoneOffset(),
    screen.colorDepth,
    screen.width + 'x' + screen.height,
    // Добавляем статический секрет (обфусцированный)
    String.fromCharCode(101, 49, 99, 97, 114, 100, 115, 101, 99, 114, 101, 116),
  ].join('|');

  // Преобразуем fingerprint в ключ
  const encoder = new TextEncoder();
  const data = encoder.encode(fingerprint);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  // Создаем CryptoKey для AES-GCM
  return crypto.subtle.importKey(
    'raw',
    hashBuffer,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
};

/**
 * Шифрует строку с использованием AES-GCM
 */
export const encrypt = async (plainText: string): Promise<string> => {
  try {
    const key = await generateKey();
    const encoder = new TextEncoder();
    const data = encoder.encode(plainText);

    // Генерируем случайный IV (Initialization Vector)
    const iv = crypto.getRandomValues(new Uint8Array(12));

    // Шифруем данные
    const encryptedBuffer = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      data,
    );

    // Объединяем IV и зашифрованные данные
    const encryptedArray = new Uint8Array(encryptedBuffer);
    const result = new Uint8Array(iv.length + encryptedArray.length);
    result.set(iv);
    result.set(encryptedArray, iv.length);

    // Конвертируем в base64 для хранения
    return btoa(String.fromCharCode.apply(null, Array.from(result)));
  } catch (error) {
    console.error('Encryption error:', error);
    // В случае ошибки возвращаем исходную строку (fallback)
    return plainText;
  }
};

/**
 * Дешифрует строку, зашифрованную функцией encrypt
 */
export const decrypt = async (encryptedText: string): Promise<string> => {
  try {
    const key = await generateKey();

    // Декодируем из base64
    const encryptedData = Uint8Array.from(atob(encryptedText), (c) =>
      c.charCodeAt(0),
    );

    // Извлекаем IV и зашифрованные данные
    const iv = encryptedData.slice(0, 12);
    const data = encryptedData.slice(12);

    // Расшифровываем
    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      data,
    );

    // Декодируем результат
    const decoder = new TextDecoder();
    return decoder.decode(decryptedBuffer);
  } catch (error) {
    console.error('Decryption error:', error);
    // В случае ошибки возвращаем исходную строку (fallback для совместимости)
    return encryptedText;
  }
};

/**
 * Проверяет, поддерживает ли браузер Web Crypto API
 */
export const isCryptoSupported = (): boolean => {
  return !!(
    window.crypto &&
    window.crypto.subtle &&
    typeof window.crypto.subtle.encrypt === 'function'
  );
};
